import { useState } from 'react';
import { HelpCircle, MessageCircle, Book, Mail, Phone, ChevronRight, Search, ExternalLink } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';

const Support = () => {
  const [search, setSearch] = useState('');

  const faqs = [
    { q: 'How do I track my project progress?', a: 'Visit the Projects page to see real-time updates on all your active projects.' },
    { q: 'What payment methods do you accept?', a: 'We accept PayPal and major credit cards for all transactions.' },
    { q: 'How long does a typical project take?', a: 'Project timelines vary based on complexity. Check the service details for estimated delivery times.' },
    { q: 'Can I request revisions?', a: 'Yes, most packages include revisions. Check your package details for specifics.' },
    { q: 'How do I contact support?', a: 'You can reach us via email at support@clouditllc.com or call us at 888-383-3213.' }
  ];

  const filteredFaqs = faqs.filter(faq => 
    faq.q.toLowerCase().includes(search.toLowerCase()) || 
    faq.a.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout currentPage="/dashboard/support">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Help & Support</h1>
        <p className="text-white/50">Find answers and get help with your account</p>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          type="text"
          data-testid="support-search"
          placeholder="Search for help..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-[#12121A] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-white focus:outline-none focus:border-[#3B82F6] transition-all"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <button
          onClick={() => toast.info('Live chat coming soon!')}
          className="bg-[#12121A] border border-white/10 rounded-2xl p-6 text-left hover:border-[#3B82F6]/50 transition-all group"
        >
          <div className="w-12 h-12 bg-[#3B82F6]/10 rounded-xl flex items-center justify-center border border-[#3B82F6]/30 mb-4 group-hover:scale-110 transition-transform">
            <MessageCircle size={24} className="text-[#3B82F6]" />
          </div>
          <h3 className="text-white font-semibold mb-1">Live Chat</h3>
          <p className="text-white/50 text-sm">Chat with our support team</p>
        </button>

        <a
          href="mailto:support@clouditllc.com"
          className="bg-[#12121A] border border-white/10 rounded-2xl p-6 text-left hover:border-[#8B5CF6]/50 transition-all group"
        >
          <div className="w-12 h-12 bg-[#8B5CF6]/10 rounded-xl flex items-center justify-center border border-[#8B5CF6]/30 mb-4 group-hover:scale-110 transition-transform">
            <Mail size={24} className="text-[#8B5CF6]" />
          </div>
          <h3 className="text-white font-semibold mb-1">Email Support</h3>
          <p className="text-white/50 text-sm">support@clouditllc.com</p>
        </a>

        <a
          href="tel:888-383-3213"
          className="bg-[#12121A] border border-white/10 rounded-2xl p-6 text-left hover:border-[#F97316]/50 transition-all group"
        >
          <div className="w-12 h-12 bg-[#F97316]/10 rounded-xl flex items-center justify-center border border-[#F97316]/30 mb-4 group-hover:scale-110 transition-transform">
            <Phone size={24} className="text-[#F97316]" />
          </div>
          <h3 className="text-white font-semibold mb-1">Phone Support</h3>
          <p className="text-white/50 text-sm">888-383-3213</p>
        </a>
      </div>

      {/* FAQs */}
      <div className="bg-[#12121A] border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#10B981]/10 rounded-xl flex items-center justify-center border border-[#10B981]/30">
            <Book size={20} className="text-[#10B981]" />
          </div>
          <h2 className="text-lg font-bold text-white">Frequently Asked Questions</h2>
        </div>

        <div className="space-y-4">
          {filteredFaqs.map((faq, i) => (
            <details
              key={i}
              className="group bg-[#0A0A0F] border border-white/10 rounded-xl overflow-hidden"
            >
              <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                <span className="text-white font-medium pr-4">{faq.q}</span>
                <ChevronRight size={20} className="text-white/40 group-open:rotate-90 transition-transform flex-shrink-0" />
              </summary>
              <div className="px-4 pb-4 text-white/60 text-sm">
                {faq.a}
              </div>
            </details>
          ))}
        </div>

        {filteredFaqs.length === 0 && (
          <div className="text-center py-8">
            <HelpCircle size={40} className="text-white/20 mx-auto mb-3" />
            <p className="text-white/50">No results found for "{search}"</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Support;
